import React, {
  type ComponentProps,
  type ComponentType,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';

import { noop } from 'lodash-es';

import type { AriaProps } from '@iconik-io/shared-lib/src/react/a11y/aria';

import type { AnyObject } from '../../../types/common';
import type { FormFieldEvent } from '../types';
import type { ValidationMessages } from '../validations/messages';
import type {
  ValidationFunctionParam,
  ValidationName,
} from '../validations/validations';

export type FormFieldValue = string | number | boolean | undefined;

export type ValidationObject = Partial<{
  [name in ValidationName]: ValidationFunctionParam;
}>;

export type ModelType = {
  [name: string]: string | number | boolean | undefined | ModelType;
};

export type WrappedComponentProps<V> = {
  name?: string;
  value?: V | undefined;
  id?: string;
  onChange?: (value: V, event?: FormFieldEvent) => void;
  onBlur?: (value: V, event: FormFieldEvent) => void;
  autoComplete?: string;
  canClear?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  validations?: string | ValidationObject;
  validationMessages?: ValidationMessages;
};

export type FieldProps<V, CP> = PropsWithChildren<{
  // If name is not specified the field won't trigger onValidSubmit of the parent form
  name?: string;
  value?: V;
  id?: string;
  className?: string;
  controlClassName?: string;
  labelClassName?: string;
  canClear?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  component: ComponentType<CP>;
  label?: string | ReactElement;
  // Shown at the end of the label row, outside the label element
  labelAdornment?: ReactNode;
  labelComponent?: ComponentType<FieldProps<V, CP> & { htmlFor?: string }>;
  // Component used in bulk metadata updates to select mode: Append, Overwrite, Remove
  updateModeComponent?: ComponentType<FieldProps<V, CP>>;
  errors?: string[];
  setInitialValue?: (value: V | undefined, props: FieldProps<V, CP>) => void;
  // Removes the field's value from the form model when it unmounts.
  unsetValue?: (props: FieldProps<V, CP>) => void;
  isFormSubmitted?: boolean;
  required?: boolean;
  hideAsterisk?: boolean;
  setValue?: (
    value: V | undefined,
    props: FieldProps<V, CP>,
    event?: FormFieldEvent,
  ) => void;
  onChange?: (value: V | undefined, event?: FormFieldEvent) => void;
  filterValue?: (value: V | undefined) => V | undefined;
  onFocus?: (value: V | undefined, event: FormFieldEvent) => void;
  onBlur?: (value: V | undefined, event: FormFieldEvent) => void;
  validate?: (
    value: V | undefined,
    props: FieldProps<V, CP>,
    event?: FormFieldEvent,
  ) => string[] | null;
  // Help message shown as a slightly dimmed text under the field
  helpMessage?: string | ReactElement;
  // Help will be shown as a field tooltip
  help?: string | ReactElement;
  // Help message will be shown as a tooltip when the user hovers over the Help Icon
  helpIconMessage?: string;
  isFormDisabled?: boolean;
  buttonAfter?: ReactElement;
  buttonAfterControlGroupClassName?: string;
  transformProps?: (props: FieldProps<V, CP>) => CP;
  type?: string;
  hideErrorMessages?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  labelWidth?: string;
  defaultValue?: V;
  isReset?: boolean;
  validations?: string | ValidationObject;
  validationMessages?: ValidationMessages;
  validateOnChange?: boolean;
  onAfterValidation?: (validationErrors: string[] | null | undefined) => void;
  aria?: AriaProps;
  vertical?: boolean;
  modifierValues?: Record<string, unknown>;
}>;

export type FormContextType = {
  setValue: <V, CP>(
    value: V,
    fieldProps: FieldProps<V, CP>,
    event?: FormFieldEvent,
  ) => void;
  setInitialValue: <V, CP>(value: V, fieldProps: FieldProps<V, CP>) => void;
  unsetValue: <V, CP>(fieldProps: FieldProps<V, CP>) => void;
  validate: <V, CP>(
    value: V,
    props: FieldProps<V, CP>,
    event?: FormFieldEvent,
  ) => string[] | null;
  errors?: Record<string, string[]>;
  isFormSubmitted: boolean;
  isFormDisabled?: boolean;
  isReset: boolean;
  resetValues: AnyObject;
  isVertical: boolean;
};

export type FieldWrapperProps<
  Component extends ComponentType<FormContextType & ComponentProps<Component>>,
> = Omit<ComponentProps<Component>, keyof FormContextType>;

export const FormContext = React.createContext<FormContextType>({
  setValue: noop,
  setInitialValue: noop,
  unsetValue: noop,
  validate: () => null,
  errors: {},
  isFormSubmitted: false,
  isFormDisabled: false,
  isReset: false,
  resetValues: {},
  isVertical: false,
});

export const FormContextProvider = FormContext.Provider;
export const FormContextConsumer = FormContext.Consumer;
